'use client';

import {
    createContext,
    Dispatch,
    type ReactNode,
    SetStateAction,
    useContext,
    useEffect,
    useState,
    useTransition,
} from 'react';
import { OAuthResponse } from '@supabase/supabase-js';
import { supabase } from '@/lib/api/utils/supabase-client';
import { User } from '@supabase/auth-js';
import { useOnboardingStore } from '@/store/onboarding';
import { signOut as signOutAction } from '@/app/api/auth/actions/actions';
import { usePathname, useRouter } from 'next/navigation';
import { getProfile } from '@/lib/requests/user';
import { SubscriptionCaps, UserDto } from '@/lib/api/schemas/user.schema';
import { enqueueUserRescan } from '@/lib/requests/worker/file-extraction.fe';
import { ConfirmationModal } from '@/components/ui/confirmation-modal';
import { ROUTES } from '@/constants/routes';
import { getSubscriptionCaps } from '@/app/api/users/actions/actions';
import { analytics } from '@/lib/analytics';

type AppContextType = {
    user: User | null;
    logInWithSocial: (
        provider: 'google' | 'apple',
        redirectToPath?: string,
    ) => Promise<OAuthResponse>;
    signOut: () => Promise<void>;
    isLoading: boolean;
    goToPage: (path: string) => void;
    loading: boolean;
    profile: UserDto | null;
    setProfile: Dispatch<SetStateAction<UserDto | null>>;
    caps: SubscriptionCaps | null;
    setConfirmLeavingRoute: (state: boolean) => void;
    fetchProfile: () => Promise<UserDto | null>;
    setIsHyperReportReady: (state: boolean) => void;
    setIsInvestorDeckReady: (state: boolean) => void;
};

const RESCAN_EVENTS = [/*'INITIAL_SESSION',*/ 'SIGNED_IN', 'TOKEN_REFRESHED'];

const LS_LAST_ACCESS = 'lastAccess';
const RESCAN_MIN_LAST_ACCESS = 15 * 60 * 1000;

const AppContext = createContext<AppContextType | undefined>(undefined);

type Props = {
    children: ReactNode;
    userData: UserDto | null;
    initCaps: SubscriptionCaps | null;
};

export function AppContextProvider({ children, userData, initCaps }: Props) {
    const pathname = usePathname();
    const [user, setUser] = useState<User | null>(null);
    const [profile, setProfile] = useState<UserDto | null>(userData);
    // TODO reload caps on websocket event
    const [caps, setCaps] = useState<SubscriptionCaps | null>(initCaps);
    const [isLoading, setIsLoading] = useState(true);
    const { clearOnboardingState } = useOnboardingStore();
    const router = useRouter();
    const [loading, startTransition] = useTransition();
    const [confirmLeavingRoute, setConfirmLeavingRoute] = useState(false);
    const [confirmModalPath, setConfirmModalPath] = useState<string | null>(
        null,
    );

    const navigateWithTransition = (path: string) => {
        setConfirmModalPath(null);
        startTransition(() => router.push(path));
    };

    const goToPage = (path: string) => {
        if (confirmLeavingRoute) setConfirmModalPath(path);
        else {
            navigateWithTransition(path);
        }
    };

    const rescanUserFiles = (accessToken: string) => {
        enqueueUserRescan(accessToken).catch((err) =>
            console.error('Failed to enqueue user file rescan', err),
        );
    };

    useEffect(() => {
        if (!isLoading) localStorage.setItem(LS_LAST_ACCESS, `${Date.now()}`);
    }, [pathname, isLoading]);

    useEffect(() => {
        const { data: listener } = supabase.auth.onAuthStateChange(
            (event, session) => {
                setUser(session?.user ?? null);
                const lastAccess = parseInt(
                    localStorage.getItem(LS_LAST_ACCESS) ?? '0',
                );
                const sinceAccess = Date.now() - lastAccess;
                setIsLoading(false);
                if (
                    session &&
                    (RESCAN_EVENTS.includes(event) ||
                        (event === 'INITIAL_SESSION' &&
                            sinceAccess > RESCAN_MIN_LAST_ACCESS))
                ) {
                    rescanUserFiles(session.access_token);
                }
            },
        );

        return () => listener?.subscription.unsubscribe();
    }, [supabase]);

    useEffect(() => {
        if (user) {
            loadProfile();
            // Identify user in PostHog when they log in
            analytics.identifyUser(user.id, {
                email: user.email,
                name: user.user_metadata?.full_name || user.user_metadata?.name,
            });
        }
    }, [user]);

    useEffect(() => {
        const updateUser = async () => {
            const { data } = await supabase.auth.getUser();
            setUser(data.user ?? null);
        };

        updateUser();
        setProfile(userData);
    }, [userData]);

    const fetchProfile = async () => {
        const { data } = await getProfile();

        setProfile(data);

        return data;
    };

    const loadProfile = async () => {
        try {
            const profile = await fetchProfile();
            // TODO add to profile endpoint returned data
            setCaps(await getSubscriptionCaps(profile.id));

            // Update PostHog with profile data (including email from database)
            if (profile && user) {
                analytics.identifyUser(user.id, {
                    email: profile.email,
                    name: profile.name,
                    role: profile.role,
                });
            }
        } catch {
            // silent
        }
    };
    const logInWithSocial = (
        provider: 'google' | 'apple',
        redirectToPath: string = ROUTES.HyperReport,
    ) => {
        const origin = window.location.origin;
        const redirectTo = `${origin}/auth/callback?next=${encodeURIComponent(redirectToPath)}`;

        return supabase.auth.signInWithOAuth({
            provider,
            options: {
                redirectTo,
            },
        });
    };

    const signOut = async () => {
        await supabase.auth.signOut();
        const { error } = await signOutAction();

        if (error) throw new Error(error);
        clearOnboardingState();
        setConfirmLeavingRoute(false);
    };

    const setIsHyperReportReady = (state: boolean) => {
        setProfile((prev) =>
            prev ? { ...prev, isHyperReportReady: state } : prev,
        );
    };
    const setIsInvestorDeckReady = (state: boolean) => {
        setProfile((prev) =>
            prev ? { ...prev, isInvestorDeckReady: state } : prev,
        );
    };

    return (
        <AppContext.Provider
            value={{
                user,
                isLoading,
                logInWithSocial,
                signOut,
                goToPage,
                loading,
                profile,
                caps,
                setProfile,
                setConfirmLeavingRoute,
                fetchProfile,
                setIsHyperReportReady,
                setIsInvestorDeckReady,
            }}
        >
            {children}
            <ConfirmationModal
                open={!!confirmModalPath}
                onConfirm={() => {
                    if (!confirmModalPath) return;
                    navigateWithTransition(confirmModalPath);
                }}
                onClose={() => setConfirmModalPath(null)}
                label={'Leave this page'}
                text={
                    'Leaving this page might result in losing your progress. Are you sure you want to proceed?'
                }
            />
        </AppContext.Provider>
    );
}

export function useAppContext() {
    const context = useContext(AppContext);
    if (context === undefined) {
        throw new Error('useApp must be used within an AppProvider');
    }
    return context;
}
